import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/ui/Sidebar';
import Header from '../../components/ui/Header';
import BackButton from '../../components/BackButton';
import ProfileHeader from './components/ProfileHeader';
import PersonalDetailsTab from './components/PersonalDetailsTab';
import EmploymentHistoryTab from './components/EmploymentHistoryTab';
import PerformanceTab from './components/PerformanceTab';
import DocumentsTab from './components/DocumentsTab';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import hrService from '../../services/hrService';
import { useAuth } from '../../contexts/AuthContext';

const EmployeeProfile = () => {
  const { userProfile } = useAuth();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('personal');
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isAddMode, setIsAddMode] = useState(false);
  const [searchParams] = useSearchParams();
  const [employeeData, setEmployeeData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [validationErrors, setValidationErrors] = useState({}); // Field-specific validation errors
  const navigate = useNavigate();

  // Get employee ID from URL parameters
  const employeeId = searchParams?.get('id');

  useEffect(() => {
    if (employeeId) {
      // Edit mode - load existing employee
      loadEmployeeData(employeeId);
      setIsAddMode(false);
    } else {
      // Add mode - create new employee template
      setIsAddMode(true);
      setIsEditingProfile(true); // Start in editing mode for new employees
      setEmployeeData(getNewEmployeeTemplate());
      setLoading(false);
    }
  }, [employeeId]);

  const loadEmployeeData = async (id) => {
    setLoading(true);
    setError('');
    try {
      const data = await hrService?.getEmployeeById(id);
      
      if (data) {
        console.log('Loaded complete employee data:', data);
        
        // FIXED: Transform to component format with proper city, state, zip, and reporting manager mapping
        const transformedData = {
          id: data?.id,
          name: data?.name || 'Unknown Employee',
          position: data?.position || '',
          department: data?.department || '',
          email: data?.email || '',
          phone: data?.phone || '',
          employeeId: data?.employeeId || '',
          username: data?.username || '',
          designation: data?.designation || '',
          branchName: data?.branchName || '',
          reportingManager: data?.reportingManager || '',
          workShift: data?.workShift || 'day',
          roleAssigned: data?.roleAssigned || '',
          workSchedule: 'Full-time, 9 AM - 6 PM',
          joinDate: data?.joinDate || '',
          dateOfJoining: data?.dateOfJoining || '',
          location: data?.personalDetails?.address?.street || '',
          profileImage: data?.profileImage || null,
          profileImageAlt: data?.profileImageAlt || '',
          salary: data?.salary || 0,
          hourlyRate: data?.hourlyRate || 0,
          employmentStatus: data?.employmentStatus || 'active',
          userId: data?.userId,
          
          // FIXED: Comprehensive personal details with proper structure
          personalDetails: {
            dateOfBirth: data?.personalDetails?.dateOfBirth || '',
            gender: data?.personalDetails?.gender || '',
            maritalStatus: data?.personalDetails?.maritalStatus || '',
            nationality: data?.personalDetails?.nationality || '',
            religion: data?.personalDetails?.religion || '',
            bloodGroup: data?.personalDetails?.bloodGroup || '',
            medicalConditions: '',
            address: {
              street: data?.personalDetails?.address?.street || '',
              city: data?.personalDetails?.address?.city || '',
              state: data?.personalDetails?.address?.state || '',
              zipCode: data?.personalDetails?.address?.zipCode || ''
            },
            emergencyContact: {
              name: data?.personalDetails?.emergencyContact?.name || data?.personalDetails?.emergencyContactName || '',
              relationship: data?.personalDetails?.emergencyContact?.relationship || data?.personalDetails?.emergencyContactRelationship || '',
              phone: data?.personalDetails?.emergencyContact?.phone || data?.personalDetails?.emergencyContactPhone || '',
              email: data?.personalDetails?.emergencyContact?.email || data?.personalDetails?.emergencyContactEmail || ''
            },
            // Banking and government details
            pfNumber: data?.personalDetails?.pfNumber || '',
            esiNumber: data?.personalDetails?.esiNumber || '',
            bankName: data?.personalDetails?.bankName || '',
            accountNumber: data?.personalDetails?.accountNumber || '',
            ifscCode: data?.personalDetails?.ifscCode || '',
            // Professional details
            educationQualification: data?.personalDetails?.educationQualification || '',
            professionalExperience: data?.personalDetails?.professionalExperience || '',
            
            // Also maintain flat structure for compatibility
            emergencyContactName: data?.personalDetails?.emergencyContactName || data?.personalDetails?.emergencyContact?.name || '',
            emergencyContactPhone: data?.personalDetails?.emergencyContactPhone || data?.personalDetails?.emergencyContact?.phone || '',
            emergencyContactRelationship: data?.personalDetails?.emergencyContactRelationship || data?.personalDetails?.emergencyContact?.relationship || '',
            emergencyContactEmail: data?.personalDetails?.emergencyContactEmail || data?.personalDetails?.emergencyContact?.email || ''
          }
        };
        
        console.log('Transformed employee data for UI with all location fields:', transformedData);
        setEmployeeData(transformedData);
      } else {
        throw new Error('Employee not found');
      }
    } catch (err) {
      console.error('Error loading employee:', err);
      setError(err?.message || 'Failed to load employee data');
    } finally {
      setLoading(false);
    }
  };

  const getNewEmployeeTemplate = () => ({
    id: null,
    name: "",
    username: "",
    password: "",
    position: "",
    department: "",
    email: "",
    phone: "",
    employeeId: "",
    manager: "",
    reportingManager: "",
    designation: "",
    branchName: "",
    workSchedule: "Full-time, 9 AM - 6 PM",
    workShift: "day",
    joinDate: new Date()?.toISOString()?.split('T')?.[0],
    dateOfJoining: new Date()?.toISOString()?.split('T')?.[0],
    location: "",
    profileImage: null,
    profileImageAlt: "",
    salary: 0,
    hourlyRate: 0,
    employmentStatus: 'active',
    userId: null,
    roleAssigned: "",
    
    // ENHANCED: Complete personal details template
    personalDetails: {
      dateOfBirth: "",
      gender: "",
      maritalStatus: "",
      nationality: "",
      religion: "",
      bloodGroup: "",
      medicalConditions: "",
      address: {
        street: "",
        city: "",
        state: "",
        zipCode: ""
      },
      emergencyContact: {
        name: "",
        relationship: "",
        phone: "",
        email: ""
      },
      // Banking and government details
      pfNumber: "",
      esiNumber: "",
      bankName: "",
      accountNumber: "",
      ifscCode: "",
      // Professional details
      educationQualification: "",
      professionalExperience: "",
      
      // Also maintain flat structure for compatibility with existing UI
      emergencyContactName: "",
      emergencyContactPhone: "",
      emergencyContactRelationship: "",
      emergencyContactEmail: ""
    }
  });

  const employmentHistory = {
    currentPosition: {
      title: "Senior Software Engineer",
      department: "Engineering",
      startDate: "March 1, 2022",
      salary: "$95,000",
      manager: "Sarah Johnson"
    },
    timeline: [
    {
      id: 1,
      type: "promotion",
      title: "Promoted to Senior Software Engineer",
      description: "Recognition for outstanding performance and leadership skills",
      date: "March 1, 2022",
      effectiveDate: "Effective immediately",
      details: {
        previous_position: "Software Engineer II",
        new_position: "Senior Software Engineer",
        salary_increase: "$15,000",
        department: "Engineering"
      },
      approvedBy: "Sarah Johnson",
      approvalDate: "February 25, 2022"
    }],
    salaryHistory: [
    { date: "March 1, 2022", amount: "$95,000", change: "+$15,000", reason: "Promotion to Senior Engineer" }]
  };

  const performanceData = {
    overallRating: 4.2,
    goalsCompleted: 8,
    totalGoals: 10,
    nextReview: "March 15, 2024",
    lastUpdated: "November 10, 2024",
    currentGoals: [],
    reviews: [],
    skills: []
  };

  const documentsData = [];

  const tabs = [
  { id: 'personal', label: 'Personal Details', icon: 'User' },
  { id: 'employment', label: 'Employment History', icon: 'Briefcase' },
  { id: 'performance', label: 'Performance', icon: 'TrendingUp' },
  { id: 'documents', label: 'Documents', icon: 'FileText' }];


  const handleProfileEdit = () => {
    // Check if user has permission to edit
    const canEdit = ['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role);
    if (!canEdit) {
      setError('You do not have permission to edit employee profiles. Only admins, HR managers, and supervisors can edit.');
      return;
    }
    
    setIsEditingProfile(true);
    
    // CRITICAL FIX: Update URL to reflect edit mode
    const currentUrl = new URL(window.location.href);
    currentUrl?.searchParams?.set('mode', 'edit');
    window.history?.replaceState({}, '', currentUrl?.toString());
  };

  // CRITICAL FIX: Enhanced handleProfileSave with better validation and error feedback
  const handleProfileSave = async (editedData, isLiveUpdate = false) => {
    // If it's a live update (real-time sync from ProfileHeader), just update local state
    if (isLiveUpdate) {
      setEmployeeData(editedData);
      // Clear validation errors for fields that now have values
      if (editedData?.name) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.name;
          return newErrors;
        });
      }
      if (editedData?.email) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.email;
          return newErrors;
        });
      }
      if (editedData?.employeeId) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.employeeId;
          return newErrors;
        });
      }
      if (editedData?.position) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.position;
          return newErrors;
        });
      }
      if (editedData?.department) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.department;
          return newErrors;
        });
      }
      if (editedData?.roleAssigned) {
        setValidationErrors(prev => {
          const newErrors = { ...prev };
          delete newErrors?.roleAssigned;
          return newErrors;
        });
      }
      return;
    }
    
    // CRITICAL: Log the data being saved for debugging
    console.log('='?.repeat(80));
    console.log('🔍 EMPLOYEE CREATION DEBUG - START');
    console.log('='?.repeat(80));
    console.log('handleProfileSave called with data:', editedData);
    console.log('isAddMode:', isAddMode, 'saving:', saving);
    
    // Prevent duplicate submissions
    if (saving) {
      console.warn('⚠️ Already saving, preventing duplicate submission');
      return;
    }
    
    setSaving(true);
    setError(''); // Clear general error
    setValidationErrors({}); // Clear validation errors
    
    try {
      if (isAddMode) {
        console.log('\n📝 Step 1: Validating required fields...');
        
        // ENHANCED: Field-specific validation with inline error messages
        const fieldErrors = {};
        
        if (!editedData?.name?.trim()) {
          fieldErrors.name = 'Name is required';
        }
        if (!editedData?.email?.trim()) {
          fieldErrors.email = 'Email is required';
        }
        if (!editedData?.employeeId?.trim()) {
          fieldErrors.employeeId = 'Employee ID is required';
        }
        if (!editedData?.position) {
          fieldErrors.position = 'Position is required';
        }
        if (!editedData?.department) {
          fieldErrors.department = 'Department is required';
        }
        if (!editedData?.roleAssigned) {
          fieldErrors.roleAssigned = 'Role is required';
        }
        
        // If there are validation errors, set them and return early (don't throw)
        if (Object.keys(fieldErrors)?.length > 0) {
          console.error('❌ Validation failed:', fieldErrors);
          setValidationErrors(fieldErrors);
          setSaving(false);
          // Scroll to first error field
          const firstErrorField = Object.keys(fieldErrors)?.[0];
          const errorElement = document.querySelector(`[name="${firstErrorField}"], [id="${firstErrorField}"]`);
          if (errorElement) {
            errorElement?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            errorElement?.focus();
          }
          return; // Don't proceed, but don't show full-page error
        }
        
        console.log('✅ All required fields present');
        console.log('\n📧 Step 2: Validating email format...');
        
        // ENHANCED: Email validation
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex?.test(editedData?.email)) {
          console.error('❌ Invalid email format:', editedData?.email);
          setValidationErrors({ email: 'Please enter a valid email address' });
          setSaving(false);
          const emailField = document.querySelector('[name="email"], [id="email"]');
          if (emailField) {
            emailField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            emailField?.focus();
          }
          return; // Don't proceed, but don't show full-page error
        }
        
        console.log('✅ Email format valid:', editedData?.email);
        console.log('\n📋 Step 2.5: Validating employment status...');
        
        // CRITICAL: Validate employment status is a valid enum value
        const validEmploymentStatuses = ['active', 'inactive', 'terminated', 'on_leave', 'resigned'];
        const employmentStatus = editedData?.employmentStatus?.trim() || 'active';
        
        if (!validEmploymentStatuses?.includes(employmentStatus)) {
          console.error('❌ Invalid employment status:', employmentStatus);
          setValidationErrors({ employmentStatus: `Invalid employment status. Must be one of: ${validEmploymentStatuses?.join(', ')}` });
          setSaving(false);
          const statusField = document.querySelector('[name="employmentStatus"], [id="employmentStatus"]');
          if (statusField) {
            statusField?.scrollIntoView({ behavior: 'smooth', block: 'center' });
            statusField?.focus();
          }
          return;
        }
        
        console.log('✅ Employment status valid:', employmentStatus);
        console.log('\n🏗️ Step 3: Building employee creation payload...');
        
        // CRITICAL: Log the complete data structure before sending to service
        console.log('Creating new employee with comprehensive data:', {
          employeeId: editedData?.employeeId,
          name: editedData?.name,
          email: editedData?.email,
          position: editedData?.position,
          department: editedData?.department,
          designation: editedData?.designation,
          roleAssigned: editedData?.roleAssigned,
          employmentStatus: employmentStatus, // Use validated status
          hasPersonalDetails: !!editedData?.personalDetails
        });
        
        // FIXED: Create new employee with comprehensive data including city, state, zip, emergency email
        const employeeCreateData = {
          // Employee table fields
          employeeId: editedData?.employeeId?.toUpperCase()?.trim(),
          position: editedData?.position?.trim(),
          department: editedData?.department?.trim(),
          hireDate: editedData?.joinDate || editedData?.dateOfJoining || new Date()?.toISOString()?.split('T')?.[0],
          salary: parseFloat(editedData?.salary || 0),
          hourlyRate: parseFloat(editedData?.hourlyRate || 0),
          employmentStatus: employmentStatus, // Use validated status
          designation: editedData?.designation?.trim() || null,
          branchName: editedData?.branchName?.trim() || null,
          workShift: editedData?.workShift || 'day',
          roleAssigned: editedData?.roleAssigned,
          reportingManagerId: editedData?.reportingManager || editedData?.reportingManagerId || null,
          supervisorId: editedData?.supervisorId || null,
          
          // User profile data
          userProfileData: {
            fullName: editedData?.name?.trim(),
            email: editedData?.email?.toLowerCase()?.trim(),
            phone: editedData?.phone?.trim() || null,
            address: editedData?.personalDetails?.address?.street || null,
            city: editedData?.personalDetails?.address?.city || null,
            state: editedData?.personalDetails?.address?.state || null,
            zip: editedData?.personalDetails?.address?.zipCode || null,
            role: 'employee'
          },

          // Include username and password for authentication
          username: editedData?.username?.trim() || null,
          password: editedData?.password || 'TempPass123!',

          // All personal details including emergency contact email
          personalDetails: {
            dateOfBirth: editedData?.personalDetails?.dateOfBirth || null,
            gender: editedData?.personalDetails?.gender || null,
            maritalStatus: editedData?.personalDetails?.maritalStatus || null,
            nationality: editedData?.personalDetails?.nationality || null,
            religion: editedData?.personalDetails?.religion || null,
            bloodGroup: editedData?.personalDetails?.bloodGroup || null,
            address: {
              street: editedData?.personalDetails?.address?.street || null,
              city: editedData?.personalDetails?.address?.city || null,
              state: editedData?.personalDetails?.address?.state || null,
              zipCode: editedData?.personalDetails?.address?.zipCode || null
            },
            emergencyContact: {
              name: editedData?.personalDetails?.emergencyContact?.name || editedData?.personalDetails?.emergencyContactName || null,
              phone: editedData?.personalDetails?.emergencyContact?.phone || editedData?.personalDetails?.emergencyContactPhone || null,
              relationship: editedData?.personalDetails?.emergencyContact?.relationship || editedData?.personalDetails?.emergencyContactRelationship || null,
              email: editedData?.personalDetails?.emergencyContact?.email || editedData?.personalDetails?.emergencyContactEmail || null
            },
            // Banking and government details
            pfNumber: editedData?.personalDetails?.pfNumber || null,
            esiNumber: editedData?.personalDetails?.esiNumber || null,
            bankName: editedData?.personalDetails?.bankName || null,
            accountNumber: editedData?.personalDetails?.accountNumber || null,
            ifscCode: editedData?.personalDetails?.ifscCode || null,
            // Professional details
            educationQualification: editedData?.personalDetails?.educationQualification || null,
            professionalExperience: editedData?.personalDetails?.professionalExperience || null,
            // Also include flat structure for compatibility
            emergencyContactName: editedData?.personalDetails?.emergencyContact?.name || editedData?.personalDetails?.emergencyContactName || null,
            emergencyContactPhone: editedData?.personalDetails?.emergencyContact?.phone || editedData?.personalDetails?.emergencyContactPhone || null,
            emergencyContactRelationship: editedData?.personalDetails?.emergencyContact?.relationship || editedData?.personalDetails?.emergencyContactRelationship || null,
            emergencyContactEmail: editedData?.personalDetails?.emergencyContact?.email || editedData?.personalDetails?.emergencyContactEmail || null
          }
        };
        
        console.log('✅ Payload built successfully');
        console.log('\n📦 Full payload structure:');
        console.log(JSON.stringify(employeeCreateData, null, 2));
        
        console.log('\n🚀 Step 4: Calling hrService.createEmployee...');
        console.log('Timestamp:', new Date()?.toISOString());
        
        // CRITICAL: Call the service and await the result
        const result = await hrService?.createEmployee(employeeCreateData);
        
        console.log('\n✅ Step 5: Service call completed');
        console.log('Result:', result);
        
        // ENHANCED: Show success message before navigation
        if (result?.data) {
          console.log('\n🎉 SUCCESS: Employee creation successful!');
          console.log('Employee data:', result?.data);
          console.log('\n🔄 Step 6: Navigating to dashboard...');
          console.log('='?.repeat(80));
          console.log('🔍 EMPLOYEE CREATION DEBUG - END (SUCCESS)');
          console.log('='?.repeat(80));
          
          // Navigate back to admin dashboard with success message
          navigate('/admin-dashboard?tab=employees&success=employee_created');
        } else {
          // If no data returned but no error, something went wrong
          console.warn('⚠️ No data returned from createEmployee, but no error thrown');
          throw new Error('Employee creation completed but no confirmation received. Please check the employee list.');
        }
        
      } else {
        // FIXED: Update existing employee with comprehensive personal details including city, state, zip, emergency email
        const updateData = {
          position: editedData?.position,
          department: editedData?.department,
          salary: parseFloat(editedData?.salary || 0),
          hourlyRate: parseFloat(editedData?.hourlyRate || 0),
          // CRITICAL: Validate employment status for updates too
          employmentStatus: (() => {
            const validStatuses = ['active', 'inactive', 'terminated', 'on_leave', 'resigned'];
            const status = editedData?.employmentStatus?.trim() || 'active';
            return validStatuses?.includes(status) ? status : 'active';
          })(),
          hireDate: editedData?.dateOfJoining || editedData?.joinDate,
          designation: editedData?.designation,
          branchName: editedData?.branchName,
          workShift: editedData?.workShift,
          roleAssigned: editedData?.roleAssigned,
          reportingManagerId: editedData?.reportingManager || editedData?.reportingManagerId,
          name: editedData?.name,
          email: editedData?.email,
          phone: editedData?.phone,
          username: editedData?.username,
          
          // Include all personal details for update
          personalDetails: editedData?.personalDetails || {}
        };
        
        console.log('Updating employee with comprehensive data including location:', updateData);
        
        const result = await hrService?.updateEmployee(employeeId, updateData);
        console.log('Employee updated successfully:', result);
        
        // Update local state with new data
        setEmployeeData({ ...employeeData, ...editedData });
        setIsEditingProfile(false);
      }
    } catch (err) {
      console.error('❌ Error saving employee:', err);
      console.error('Error details:', {
        message: err?.message,
        code: err?.code,
        stack: err?.stack
      });
      
      // ENHANCED: More user-friendly error messages with better formatting
      let errorMessage = err?.message || 'Failed to save employee data';
      
      // Handle specific error types with user-friendly messages
      if (err?.code === 'DUPLICATE_EMAIL' || err?.message?.toLowerCase()?.includes('email already exists')) {
        const existingEmployee = err?.existingEmployee;
        if (existingEmployee?.name) {
          errorMessage = `Email already exists: This email is already registered for ${existingEmployee?.name} (${existingEmployee?.id || 'Employee ID: ' + existingEmployee?.id || ''}). Please use a different email address.`;
        } else {
          errorMessage = `Email already exists: This email address is already in use. Please use a different email.`;
        }
      } else if (err?.code === 'DUPLICATE_EMPLOYEE_ID' || err?.message?.toLowerCase()?.includes('employee id already exists')) {
        const existingEmployee = err?.existingEmployee;
        if (existingEmployee?.name) {
          errorMessage = `Employee ID already exists: The Employee ID "${editedData?.employeeId || ''}" is already assigned to ${existingEmployee?.name} (Status: ${existingEmployee?.status || 'unknown'}). Please use a different Employee ID.`;
        } else {
          errorMessage = `Employee ID already exists: This Employee ID is already in use. Please use a different Employee ID.`;
        }
      } else if (err?.message?.includes('duplicate key value violates unique constraint')) {
        // Handle database constraint violations
        if (err?.message?.includes('users_email_partial_key')) {
          errorMessage = `Email already exists: This email address is already registered. Please use a different email.`;
        } else if (err?.message?.includes('employee_id') || err?.message?.includes('employees')) {
          errorMessage = `Employee ID already exists: This Employee ID is already in use. Please use a different Employee ID.`;
        } else {
          errorMessage = `Duplicate entry detected: ${err?.message}`;
        }
      } else if (err?.message?.includes('required') || err?.code === 'INVALID_EMPLOYEE_ID_FORMAT') {
        errorMessage = err?.message; // Use the validation error message directly
      } else if (err?.message?.includes('email') || err?.code === 'INVALID_EMAIL_FORMAT') {
        errorMessage = `Invalid email format: ${err?.message}`;
      } else if (err?.message?.includes('validation')) {
        errorMessage = `Validation error: ${err?.message}`;
      } else if (err?.message?.includes('Failed to create employee')) {
        // Extract the actual error from the database message
        const match = err?.message?.match(/Failed to create employee: (.+)/);
        if (match && match?.[1]) {
          errorMessage = match?.[1];
        } else {
          errorMessage = err?.message;
        }
      }
      
      setError(errorMessage);
      
      // CRITICAL: Scroll to top to show error message
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } finally {
      setSaving(false);
    }
  };

  const handleProfileCancel = () => {
    if (isAddMode) {
      // In add mode, navigate back to admin dashboard
      navigate('/admin-dashboard');
    } else {
      // In edit mode, cancel editing and update URL
      setIsEditingProfile(false);
      
      // CRITICAL FIX: Update URL to reflect view mode
      const currentUrl = new URL(window.location.href);
      currentUrl?.searchParams?.set('mode', 'view');
      window.history?.replaceState({}, '', currentUrl?.toString());
    }
  };

  const handleImageUpload = () => {
    // Handle image upload logic here
    console.log('Opening image upload dialog');
  };

  const handleGoBack = () => {
    // Navigate based on user role
    if (['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role)) {
      navigate('/admin-dashboard');
    } else {
      navigate('/employee-dashboard');
    }
  };

  // FIXED: Enhanced personal details save handler - moved before renderTabContent
  const handlePersonalDetailsSave = async (personalDetailsData) => {
    setSaving(true);
    setError('');
    
    try {
      console.log('Saving personal details:', personalDetailsData);
      
      if (isAddMode) {
        // In add mode, just update local state
        setEmployeeData({ ...employeeData, ...personalDetailsData });
      } else {
        // In edit mode, save to database
        const result = await hrService?.updateEmployee(employeeId, personalDetailsData);
        console.log('Personal details saved successfully:', result);
        
        // Update local state with saved data
        setEmployeeData({ ...employeeData, ...personalDetailsData });
        
        // Optionally reload the employee data to ensure consistency
        await loadEmployeeData(employeeId);
      }
    } catch (err) {
      console.error('Error saving personal details:', err);
      setError(err?.message || 'Failed to save personal details');
    } finally {
      setSaving(false);
    }
  };

  const renderTabContent = () => {
    if (!employeeData) return null;
    
    switch (activeTab) {
      case 'personal':
        return (
          <PersonalDetailsTab 
            profile={employeeData} 
            employee={employeeData} 
            onUpdate={(updatedData) => setEmployeeData({ ...employeeData, ...updatedData })}
            canEdit={isEditingProfile}
            onSave={handlePersonalDetailsSave}
            onCancel={() => setIsEditingProfile(false)}
            saving={saving}
          />
        );
      case 'employment':
        return <EmploymentHistoryTab employmentHistory={employmentHistory} employee={employeeData} />;
      case 'performance':
        return <PerformanceTab performanceData={performanceData} canViewDetails={true} />;
      case 'documents':
        return <DocumentsTab documents={documentsData} canUpload={true} canDownload={true} />;
      default:
        return (
          <PersonalDetailsTab 
            profile={employeeData} 
            employee={employeeData} 
            onUpdate={(updatedData) => setEmployeeData({ ...employeeData, ...updatedData })}
            canEdit={isEditingProfile}
            onSave={handlePersonalDetailsSave}
            onCancel={() => setIsEditingProfile(false)}
            saving={saving}
          />
        );
    }
  };

  // Show error state ONLY for actual loading/network errors (not validation errors)
  // Validation errors are shown inline in the form, not as a full-page error
  // Only show full-page error in edit mode when loading fails, not in add mode
  if (error && !loading && !isAddMode && employeeId) {
    // Only show full-page error for loading errors in edit mode
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <Header sidebarCollapsed={sidebarCollapsed} />
        <main className={`
          transition-smooth pt-16
          ${sidebarCollapsed ? 'ml-16' : 'ml-240'} 
          max-md:ml-0
        `}>
          <div className="p-6">
            <div className="flex items-center justify-between mb-6">
              <BackButton 
                onClick={handleGoBack}
                label={['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role) 
                  ? "Back to Admin Dashboard" :"Back to Dashboard"}
                variant="outline"
                size="sm"
              />
            </div>
            
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="text-center">
                <Icon name="AlertCircle" size={48} color="var(--color-destructive)" className="mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-destructive mb-2">Error Loading Employee</h3>
                <p className="text-muted-foreground mb-4">{error}</p>
                <div className="flex items-center justify-center gap-3">
                  <Button onClick={handleGoBack} variant="outline">
                    Back to Dashboard
                  </Button>
                  <Button onClick={() => {
                    setError('');
                    if (employeeId) {
                      loadEmployeeData(employeeId);
                    }
                  }}>
                    Try Again
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
        <Header sidebarCollapsed={sidebarCollapsed} />
        <main className={`
          transition-smooth pt-16
          ${sidebarCollapsed ? 'ml-16' : 'ml-240'} 
          max-md:ml-0
        `}>
          <div className="p-6">
            <div className="flex items-center justify-center h-96">
              <div className="text-center">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
                <p className="text-muted-foreground">Loading employee profile...</p>
              </div>
            </div>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Sidebar isCollapsed={sidebarCollapsed} onToggle={() => setSidebarCollapsed(!sidebarCollapsed)} />
      <Header sidebarCollapsed={sidebarCollapsed} />
      <main className={`
        transition-smooth pt-16
        ${sidebarCollapsed ? 'ml-16' : 'ml-240'} 
        max-md:ml-0
      `}>
        <div className="p-6">
          {/* Back Button and Breadcrumb */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <BackButton 
                onClick={handleGoBack}
                label={['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role) 
                  ? "Back to Admin Dashboard" :"Back to Dashboard"}
                variant="outline"
                size="sm"
              />
              {/* Only show breadcrumb for admin/hr_manager/supervisor roles */}
              {['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role) && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <span>HR Portal</span>
                  <Icon name="ChevronRight" size={16} />
                  <span>Admin Dashboard</span>
                  <Icon name="ChevronRight" size={16} />
                  <span className="text-foreground font-medium">
                    {isAddMode ? 'Add New Employee' : 'Employee Profile'}
                  </span>
                </div>
              )}
            </div>
            
            {/* Action Buttons */}
            {isAddMode && !isEditingProfile && (
              <div className="flex items-center gap-2">
                <Button onClick={handleProfileEdit} variant="default" size="sm">
                  <Icon name="Edit" size={16} className="mr-2" />
                  Continue Adding Employee
                </Button>
              </div>
            )}
          </div>

          {/* Show error message if there's an error during saving */}
          {error && (
            <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6">
              <div className="flex items-center gap-2">
                <Icon name="AlertCircle" size={16} color="var(--color-destructive)" />
                <p className="text-destructive font-medium">Error</p>
              </div>
              <p className="text-destructive/80 text-sm mt-1">{error}</p>
            </div>
          )}

          {/* Profile Header */}
          <ProfileHeader
            employee={employeeData}
            isEditing={isEditingProfile}
            onEdit={handleProfileEdit}
            onSave={handleProfileSave}
            onCancel={handleProfileCancel}
            onImageUpload={handleImageUpload}
            isAddMode={isAddMode}
            saving={saving}
            canEdit={['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role)}
          />

          {/* CRITICAL FIX: Show PersonalDetailsTab in both Add Mode and Edit Mode */}
          <div className="bg-card rounded-lg border border-border mb-6">
            {/* ENHANCED: Add helpful section header for Add Mode */}
            {isAddMode && (
              <div className="p-6 pb-0">
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={16} color="var(--color-primary)" />
                    <p className="text-sm font-medium text-blue-800">
                      Complete Employee Profile - Fill in all available information below (optional fields can be added later)
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Show tabs only for existing employees, not in add mode */}
            {!isAddMode && (
              <div className="border-b border-border">
                <nav className="flex space-x-8 px-6" aria-label="Profile sections">
                  {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`
                        flex items-center gap-2 py-4 px-1 border-b-2 font-medium text-sm transition-colors
                        ${activeTab === tab?.id ?
                    'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-muted'}
                      `
                    }>
                      <Icon name={tab?.icon} size={16} />
                      <span className="hidden sm:inline">{tab?.label}</span>
                    </button>
                  ))}
                </nav>
              </div>
            )}

            {/* CRITICAL FIX: Always show PersonalDetailsTab - in add mode show directly without tabs */}
            <div className="p-6">
              {isEditingProfile && !isAddMode && (
                <div className="mb-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center gap-2">
                    <Icon name="Info" size={16} color="var(--color-primary)" />
                    <p className="text-sm font-medium text-primary">
                      Edit Mode Active - All fields below are now editable. Make your changes and click "Save Changes" when done.
                    </p>
                  </div>
                </div>
              )}
              
              {/* CRITICAL: In add mode, always show PersonalDetailsTab; in edit mode, show based on active tab */}
              {isAddMode ? (
                <PersonalDetailsTab 
                  profile={employeeData} 
                  employee={employeeData} 
                  onUpdate={(updatedData) => setEmployeeData({ ...employeeData, ...updatedData })}
                  canEdit={isEditingProfile}
                  onSave={handlePersonalDetailsSave}
                  onCancel={() => setIsEditingProfile(false)}
                  saving={saving}
                />
              ) : (
                renderTabContent()
              )}
            </div>
          </div>

          {/* FIXED: Consolidated Action Buttons at the bottom - Only show when editing */}
          {isEditingProfile && (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {isAddMode ? 'Add New Employee' : 'Edit Employee Profile'}
                  </h3>
                  <p className="text-sm text-muted-foreground">
                    {isAddMode 
                      ? 'Fill in all required information in the ProfileHeader above and click "Save Employee" to create the profile.'
                      : 'Review your changes above and click "Save Changes" to update the profile.'
                    }
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button 
                    onClick={handleProfileCancel} 
                    variant="outline" 
                    size="default"
                    disabled={saving}
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={() => handleProfileSave(employeeData)} 
                    variant="default" 
                    size="default"
                    disabled={saving}
                  >
                    {saving ? (
                      <>
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                        Saving...
                      </>
                    ) : (
                      <>
                        <Icon name="Save" size={16} className="mr-2" />
                        {isAddMode ? 'Save Employee' : 'Save Changes'}
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </div>
          )}

          {/* Edit Profile Button for existing employees when not in edit mode */}
          {!isAddMode && !isEditingProfile && ['admin', 'hr_manager', 'supervisor']?.includes(userProfile?.role) && (
            <div className="bg-card rounded-lg border border-border p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-lg font-semibold text-foreground mb-2">Employee Profile Management</h3>
                  <p className="text-sm text-muted-foreground">
                    View and manage this employee's profile information and personal details.
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Button onClick={handleProfileEdit} variant="default" size="default">
                    <Icon name="Edit" size={16} className="mr-2" />
                    Edit Profile
                  </Button>
                </div>
              </div>
            </div>
          )}
        </div>
      </main>
    </div>
  );

};

export default EmployeeProfile;